import { useEffect, useState } from "react";
import {
  Sidebar,
  SidebarHeader,
  SidebarContent,
  SidebarFooter,
  SidebarMenuSub,
  SidebarMenuSubItem,
} from "@/components/ui/sidebar";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Link, useLocation, useNavigate } from "react-router";
import { useTheme } from "next-themes";
import { ArrowUp, ChevronDown, Link2, LogOut, Moon, Plus } from "lucide-react";
import { useAuth, useLogout } from "@/hooks/useAuth";
import {
  ACCESO_RAPIDO,
  ACCESO_SECUNDARIO,
  claveDeItem,
  esGrupo,
  esRutaActiva,
  FOOTER_ITEMS,
  filtrarItems,
  NAV_ITEMS,
  type EnlaceDeMenu,
  type GrupoDeMenu,
  type ItemDeMenu,
} from "@/lib/menu";
import { obtenerIniciales } from "@/lib/iniciales";
import { REFERENCIAS_DE_MERCADO } from "@/lib/modulos";
import { puedeAcceder } from "@/lib/permisos";
import { cn } from "@/lib/utils";

/* Escala de la fila del menú. Es COMPACTA a propósito, y el presupuesto no lo
   fija la pantalla sino el ESCALADO: los equipos donde se usa son Windows 11
   con el tamaño de letra al 150 %, así que un monitor grande rinde unos 600 px
   de alto útil. Son ocho filas de navegación más sus submódulos, el acceso
   rápido, el identificador y los controles de sesión — a 36 px la fila no
   entraban. 32 px (14 px de texto sobre 6 px de aire) es la densidad de un menú
   de aplicación y sigue siendo un blanco cómodo de clic. */
const rowBase =
  "flex items-center gap-2.5 rounded-lg px-3 py-1.5 text-sm leading-5 font-semibold transition-colors";
const rowActiva =
  "bg-card font-bold text-foreground shadow-[0_4px_14px_-8px_rgba(20,22,26,0.18)]";
const rowInactiva = "text-muted-foreground hover:bg-card";

function Punto({ activo }: { activo: boolean }) {
  return (
    <span
      className={`h-1.5 w-1.5 rounded-full ${activo ? "bg-primary" : "bg-transparent"}`}
    />
  );
}

/**
 * Dónde está parado el usuario: la ruta Y su query string.
 *
 * Las dos, porque hay pantallas compartidas cuya ruta no alcanza para saber en
 * qué sección se está trabajando — el origen viaja en la query (ver
 * `lib/origenDeNavegacion.ts`). Va como un objeto y no como dos props sueltas
 * para que sumar un dato de ubicación no obligue a tocar cuatro firmas.
 */
interface Ubicacion {
  pathname: string;
  search: string;
}

function FilaDeEnlace({ item, ubicacion }: { item: EnlaceDeMenu; ubicacion: Ubicacion }) {
  const activo = esRutaActiva(ubicacion.pathname, item.url, ubicacion.search);
  return (
    /* `aria-current` y no solo el color: el resaltado es la única señal de
       "estás acá" y un lector de pantalla no ve el fondo blanco. */
    <Link
      to={item.url}
      aria-current={activo ? "page" : undefined}
      className={`${rowBase} ${activo ? rowActiva : rowInactiva}`}
    >
      <Punto activo={activo} />
      {item.label}
    </Link>
  );
}

/**
 * Rótulo de módulo con submódulos. La fila ENTERA es el disparador del
 * desplegable: no navega a ninguna parte (ver la regla en `lib/menu.ts`), así
 * que un clic en el texto tiene que hacer lo mismo que un clic en el chevron.
 *
 * Se despliega solo cuando la ruta actual cae dentro del grupo, y desde ahí el
 * usuario puede volver a cerrarlo.
 */
function FilaDeGrupo({ item, ubicacion }: { item: GrupoDeMenu; ubicacion: Ubicacion }) {
  const enElGrupo = item.hijos.some((hijo) =>
    esRutaActiva(ubicacion.pathname, hijo.url, ubicacion.search),
  );
  const [abierto, setAbierto] = useState(enElGrupo);

  useEffect(() => {
    if (enElGrupo) setAbierto(true);
  }, [enElGrupo]);

  return (
    <Collapsible open={abierto} onOpenChange={setAbierto}>
      <CollapsibleTrigger asChild>
        {/* El nombre accesible del botón es el rótulo, no "Submenú de …": lo
            que se anuncia es el módulo, y Radix agrega `aria-expanded`. */}
        <button type="button" className={`${rowBase} ${rowInactiva} w-full pr-2 text-left`}>
          <Punto activo={false} />
          <span className="flex-1">{item.label}</span>
          <ChevronDown
            className={`h-4 w-4 transition-transform ${abierto ? "rotate-180" : ""}`}
          />
        </button>
      </CollapsibleTrigger>

      <CollapsibleContent>
        {/* La lista se llama como el módulo: el rótulo YA dice "Mantenedor de
            …", así que "Submenú de …" encima solo agregaría ruido. */}
        <SidebarMenuSub aria-label={item.label} className="mt-0.5 gap-0.5">
          {item.hijos.map((hijo) => {
            const hijoActivo = esRutaActiva(ubicacion.pathname, hijo.url, ubicacion.search);
            return (
              <SidebarMenuSubItem key={hijo.url}>
                <Link
                  to={hijo.url}
                  aria-current={hijoActivo ? "page" : undefined}
                  className={`flex items-center gap-2.5 rounded-lg px-3 py-1 text-[0.8125rem] leading-5 font-semibold transition-colors ${
                    hijoActivo ? rowActiva : rowInactiva
                  }`}
                >
                  <Punto activo={hijoActivo} />
                  {hijo.label}
                </Link>
              </SidebarMenuSubItem>
            );
          })}
        </SidebarMenuSub>
      </CollapsibleContent>
    </Collapsible>
  );
}

/** Una fila del menú: enlace o rótulo de módulo, según lo que declare el ítem. */
function FilaDeMenu({ item, ubicacion }: { item: ItemDeMenu; ubicacion: Ubicacion }) {
  return esGrupo(item) ? (
    <FilaDeGrupo item={item} ubicacion={ubicacion} />
  ) : (
    <FilaDeEnlace item={item} ubicacion={ubicacion} />
  );
}

export function AppSidebar() {
  const { pathname, search } = useLocation();
  // El resaltado mira las dos cosas: hay pantallas compartidas cuya ruta no
  // dice en qué sección se está trabajando (ver `esRutaActiva`).
  const ubicacion: Ubicacion = { pathname, search };
  const navigate = useNavigate();
  const { resolvedTheme, setTheme } = useTheme();
  const isDark = resolvedTheme === "dark";
  const logout = useLogout();
  /* Quién tiene la sesión, según la API. El sidebar cuelga de `ProtectedRoute`,
     que no renderiza nada hasta resolver `GET /api/user`, así que acá el
     usuario ya está: no hay parpadeo de datos vacíos. */
  const { user } = useAuth();
  const perfil = user?.perfil ?? null;

  /* El menú muestra solo lo que el perfil puede abrir — ítems y submódulos
     (ver `filtrarItems`). Es la otra mitad de RequiereRol: la guarda impide
     entrar, esto evita ofrecer la puerta. */
  const navVisibles = filtrarItems(NAV_ITEMS, perfil);
  const footerVisibles = filtrarItems(FOOTER_ITEMS, perfil);
  /* El acceso rápido pasa por la MISMA guarda que las filas: es un destino más,
     solo que dibujado como botón. */
  const accesoRapidoVisible = puedeAcceder(perfil, ACCESO_RAPIDO.url);
  /* El módulo se puede apagar para un despliegue (ver `lib/modulos.ts`). Va
     ANTES del permiso a propósito: apagado, no lo ve NADIE —tampoco el Super
     Admin—, porque lo que falta no es autorización sino el módulo. */
  const accesoSecundarioVisible =
    REFERENCIAS_DE_MERCADO && puedeAcceder(perfil, ACCESO_SECUNDARIO.url);

  return (
    <Sidebar>
      <SidebarHeader className="gap-0 px-5 pt-4">
        {/* Logo. Lleva a la primera pantalla del usuario, la cola de informes:
            la portada de accesos se eliminó y "/" hoy solo redirige ahí. */}
        <Link to="/informes" className="flex items-center gap-2.5">
          <img src="/consultek-mark.png" alt="Consultek" className="h-7 w-7 object-contain" />
          <span className="text-lg font-extrabold tracking-tight">
            Consul<span className="text-primary">tek</span>
          </span>
        </Link>

        {/* Perfil: quién tiene la sesión y con qué perfil entra. Sale del
            usuario autenticado, el MISMO dato que filtra el menú de abajo —
            antes esto era un selector de perfil simulado de la demo, y podía
            decir una cosa distinta de la que la API sostenía. */}
        <div className="mt-3 flex flex-col items-center text-center">
          <div className="relative">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-br from-[#c9ead0] to-[#8fd39a] text-[0.9375rem] font-bold text-[#2f7a2e] shadow-[0_6px_18px_-8px_rgba(76,182,72,0.6)]">
              {obtenerIniciales(user?.name ?? "")}
            </div>
            <span className="absolute -bottom-0.5 -right-0.5 flex h-4 w-4 items-center justify-center rounded-full border-2 border-sidebar bg-primary text-white">
              <ArrowUp className="h-2 w-2" strokeWidth={2.8} />
            </span>
          </div>
          <div className="mt-1.5 text-sm font-bold leading-tight">{user?.name}</div>
          {/* El NOMBRE del perfil, que es lo que el usuario reconoce; quien
              decide qué se ofrece es su `codigo` (ver `lib/permisos.ts`). */}
          <div className="mt-0.5 text-[0.6875rem] leading-4 text-muted-foreground">{perfil?.nombre}</div>
        </div>
      </SidebarHeader>

      <SidebarContent className="px-3">
        <div className="my-2.5 h-px bg-sidebar-border" />

        {/* Acceso rápido, POR SOBRE el menú y como botón: crear un informe es la
            acción de arranque del proceso, no un destino que se busca entre las
            demás filas. Queda fuera del <nav> a propósito — la navegación
            enumera pantallas; esto es una acción destacada.

            `size="sm"` y no el tamaño por defecto: es el tamaño chico del DS
            (36 px en vez de 44), no un botón achicado a mano. Sigue siendo el
            único botón sólido del menú, así que destaca por COLOR y no por
            alto. */}
        {accesoRapidoVisible && (
          <Button asChild size="sm" className="w-full">
            <Link to={ACCESO_RAPIDO.url}>
              <Plus className="size-4" strokeWidth={2.6} />
              {ACCESO_RAPIDO.label}
            </Link>
          </Button>
        )}

        {/* El segundo acceso, con borde y no sólido: el de arriba sigue siendo
            el único botón sólido del menú. Enlazar referencias es una acción
            sobre un informe que YA existe —un paso de adentro del proceso—, no
            el arranque, y el peso visual lo dice sin necesidad de una frase.

            PERO EL BORDE ES VERDE, no gris. Con el `outline` de fábrica los dos
            accesos no se leían como pareja: arriba un botón verde y abajo uno
            neutro, que es el mismo tratamiento que reciben los botones
            secundarios de cualquier formulario. Comparten familia de color y se
            distinguen por PESO —sólido contra contorno—, que es lo que dice
            "esto también es una puerta de arranque, pero la segunda".

            Los dos verdes salen de tokens: `accent-foreground` es el verde
            oscuro del texto (#2f7a2e) y el borde es el primario rebajado, así
            que los dos acompañan al modo oscuro solos.

            El borde SE LLENA cuando la pantalla está abierta, como el diseño:
            el botón vive por sobre el menú, donde no hay ninguna fila que se
            encienda para decir dónde estás parado.

            Mismo ícono de eslabón que el DS reserva para enlazar. */}
        {accesoSecundarioVisible && (
          <Button
            asChild
            size="sm"
            variant="outline"
            className={cn(
              "text-accent-foreground w-full hover:border-primary hover:text-accent-foreground",
              esRutaActiva(ubicacion.pathname, ACCESO_SECUNDARIO.url, ubicacion.search)
                ? "border-primary bg-accent"
                : "border-primary/30",
            )}
          >
            <Link to={ACCESO_SECUNDARIO.url}>
              <Link2 className="size-4" strokeWidth={2.2} />
              {ACCESO_SECUNDARIO.label}
            </Link>
          </Button>
        )}

        {/* UN SOLO menú. Los dos bloques que declara `lib/menu.ts` se dibujan
            en la MISMA columna, separados por una línea. Antes el segundo vivía
            anclado al borde inferior (`SidebarFooter`), y el hueco elástico
            entre ambos era espacio muerto en pantallas altas y lo primero que
            desbordaba en las bajas: el menú entero no entraba. Lo que cambia es
            dónde se apoyan, no su orden ni su significado — el pie sigue yendo
            de lo más general a lo más restringido.

            Un grupo cuyos hijos se fueron todos en el filtro ya no llega acá:
            `filtrarItems` lo descarta, porque un rótulo de módulo sin pantallas
            sería una fila que no abre nada. */}
        {/* CON NOMBRE, y hace falta desde que la miga vive en toda pantalla:
            con dos `<nav>` sin rotular, un lector de pantalla anuncia
            "navegación" dos veces y quien recorre por regiones no sabe cuál es
            cuál. La miga trae el suyo del componente del DS. */}
        <nav aria-label="Menú principal" className="mt-2.5 flex flex-col gap-0.5">
          {navVisibles.map((item) => (
            <FilaDeMenu key={claveDeItem(item)} item={item} ubicacion={ubicacion} />
          ))}

          {/* La línea reemplaza al hueco: mantiene la distinción entre el
              trabajo del día a día y el resto por 13 px en vez de por todo el
              alto sobrante. Solo si hay algo debajo que separar. */}
          {footerVisibles.length > 0 && <div className="mx-3 my-1.5 h-px bg-sidebar-border" />}

          {footerVisibles.map((item) => (
            <FilaDeMenu key={claveDeItem(item)} item={item} ubicacion={ubicacion} />
          ))}
        </nav>
      </SidebarContent>

      {/* El pie ya NO es la mitad de abajo del menú: quedan solo los controles
          de la sesión, que no son destinos de navegación. Anclado al borde
          inferior a propósito — cuando el menú no entra, lo que se desplaza es
          `SidebarContent` (tiene `overflow-auto`) y estos dos quedan siempre a
          la vista.

          Los DOS en una sola fila. Ocupaban dos filas y una línea divisoria
          —86 px— para dos acciones que se usan una vez por sesión; ahora son
          28. El interruptor conserva su tamaño del DS (44×24, no se achica un
          componente del sistema) y la luna dice QUÉ controla: es el trabajo que
          hacía el rótulo "Tema", que era la parte cara. */}
      <SidebarFooter className="gap-0 px-3 pb-3">
        {/* Cierra la navegación: marca dónde termina la lista que se desplaza. */}
        <div className="mx-3 mb-1.5 h-px bg-sidebar-border" />

        <div className="flex items-center justify-between gap-2 px-1">
          <button
            type="button"
            onClick={() =>
              logout.mutate(undefined, {
                onSuccess: () => navigate("/login", { replace: true }),
              })
            }
            disabled={logout.isPending}
            className="flex items-center gap-2 rounded-lg px-2 py-1 text-[0.8125rem] font-semibold leading-5 text-foreground transition-colors hover:bg-card"
          >
            <LogOut className="h-4 w-4" />
            Cerrar sesión
          </button>

          {/* La luna es decorativa: quien nombra al control es el `aria-label`
              del interruptor, y un icono anunciado aparte solo repetiría. */}
          <span className="flex items-center gap-1.5 text-muted-foreground">
            <Moon className="h-3.5 w-3.5" aria-hidden="true" />
            <Switch
              checked={isDark}
              onCheckedChange={(c) => setTheme(c ? "dark" : "light")}
              aria-label="Cambiar tema"
            />
          </span>
        </div>
      </SidebarFooter>
    </Sidebar>
  );
}
